import {
	IonButton,
	IonButtons,
	IonContent,
	IonHeader,
	IonIcon,
	IonImg,
	IonInfiniteScroll,
	IonInfiniteScrollContent,
	IonInput,
	IonItem,
	IonLabel,
	IonList,
	IonLoading,
	IonMenu,
	IonMenuToggle,
	IonPage,
	IonText,
	IonTitle,
	IonToast,
	IonToolbar,
} from "@ionic/react";
import { flatten, get, isEmpty, isNil, map } from "lodash";
import { useEffect, useState } from "react";
import { getSearchResults, getSearchResultsByText, rateSearch } from "../../../api";
import usePartSearch from "../../../context/search.context";
import { Swiper, SwiperSlide } from "swiper/react";
import { arrowBackOutline, thumbsDown } from "ionicons/icons";
import "swiper/swiper.min.css";
import "@ionic/react/css/ionic-swiper.css";
import { catchError, EMPTY, switchMap } from "rxjs";
import { useHistory } from "react-router";
import useAuth from "../../../context/auth.context";
import { useTranslation } from "react-i18next";
import { DEFAULT_PAGE_SIZE, DEFAULT_TOAST_DURATION } from '../../../constants';

export function SearchResults() {
	const [loading, setLoading] = useState<boolean>(false);
	const [pageNumber, setPageNumber] = useState<number>(1);
	const [cachedAccessToken, setCachedAccessToken] = useState<string>("");
	const [searchEmbedding, setSearchEmbedding] = useState<string>("");
	const [toastMessage, setToastMessage] = useState<string>("");
	const [disableInfiniteScroll, setDisableInfiniteScroll] = useState<boolean>(false);

	const history = useHistory();
	const { t } = useTranslation();

	const {
		currentImage,
		isTextSearch,
		drawing,
		newSearch,
		searchId,
		searchResults,
		searchTerm,
		setSearchResults,
		setSearchTerm,
	} = usePartSearch();
	const { acquireToken, username, validateUser } = useAuth();
	const auth = useAuth();

	useEffect(() => {
		console.log("SearchResults: useEffect validateUser", username);
		validateUser(() => history.push('/signin'));
	}, [username]);

	useEffect(() => {
		let searchResults$;
		if (!isNil(searchTerm) && searchTerm !== "") {
			setLoading(true);
			searchResults$ = acquireToken()
				.pipe(
					switchMap((accessToken) => {
						setCachedAccessToken(accessToken);
						return getSearchResultsByText(
							{
								accessToken,
								searchTerm,
								username: username!,
								page: pageNumber,
								pageSize: DEFAULT_PAGE_SIZE,
							}
						).pipe(
							catchError(() => {
								setLoading(false);
								setToastMessage(t("searchResults.searchResultsError"));
								return EMPTY;
							})
						);
					}
					));
		} else if (
			(currentImage &&
				currentImage.blobPath &&
				searchId &&
				searchResults.length === 0)
		) {
			setLoading(true);
			searchResults$ = acquireToken()
				.pipe(
					switchMap((accessToken) => {
						setCachedAccessToken(accessToken);
						return getSearchResults(
							{
								accessToken,
								drawing: flatten(drawing),
								embedding: searchEmbedding,
								page: pageNumber,
								pageSize: DEFAULT_PAGE_SIZE,
								searchId,
								searchTerm,
								username: username!,
								webPath: currentImage.blobPath!,
								useOcr: isTextSearch,
							}
						).pipe(
							catchError(() => {
								setLoading(false);
								setToastMessage(t("searchResults.searchResultsError"));
								return EMPTY;
							})
						);
					})
				)
		}

		if (searchResults$) {
			searchResults$.subscribe((results) => {
				setSearchResults(results.data);
				setSearchEmbedding(results.embeddings);
				setLoading(false);
			});
		}
		return () => {
			setLoading(false);
		};
	}, [searchId]);

	function onInfiniteScroll(event: any) {
		const newPageNumber = pageNumber + 1;
		setPageNumber(newPageNumber);

		if (
			!isNil(currentImage)
			&& !isNil(currentImage.blobPath)
			&& searchId
			&& searchResults.length !== 0
		) {
			getSearchResults(
				{
					accessToken: cachedAccessToken,
					drawing: flatten(drawing),
					embedding: searchEmbedding,
					page: newPageNumber,
					pageSize: DEFAULT_PAGE_SIZE,
					searchId,
					searchTerm,
					useOcr: isTextSearch,
					username: username!,
					webPath: currentImage.blobPath!,
				}
			).pipe(
				catchError(() => {
					event.target.complete();
					return EMPTY;
				})
			).subscribe((results) => {
				const receivedImages = get(results, 'data', []);
				if (receivedImages.length < DEFAULT_PAGE_SIZE) {
					setDisableInfiniteScroll(true);
				}
				setSearchResults([...searchResults, ...receivedImages]);
				setSearchEmbedding(results.embeddings);
				event.target.complete();
			});
		}
	}

	function onFilterChange(event: any) {
		const newTerm = event.target.value;
		setSearchTerm(newTerm);
		if (searchId && currentImage && currentImage.blobPath) {
			setLoading(true);
			return getSearchResults(
				{
					accessToken: cachedAccessToken,
					drawing: flatten(drawing),
					embedding: searchEmbedding,
					page: pageNumber,
					pageSize: DEFAULT_PAGE_SIZE,
					searchId,
					searchTerm,
					useOcr: isTextSearch,
					webPath: currentImage.blobPath!,
					username: username!,
				}
			).pipe(
				catchError(() => {
					setLoading(false);
					return EMPTY;
				})
			)
				.subscribe((results) => {
					setSearchResults(results.data);
					setSearchEmbedding(results.embeddings);
					setLoading(false);
				});
		}
	}

	function onClickSlide(barcode: string, index: number) {
		history.push(
			`/search-detail/${barcode}?searchIndex=${index}&searchId=${searchId}`
		);
	}

	function onReject() {
		if (searchId) {
			setLoading(true);
			return rateSearch("", searchId, "-1", cachedAccessToken)
				.subscribe(() => {
					newSearch();
					setLoading(false);
					history.push("/search");
				});
		} else {
			newSearch();
			history.push("/search");
		}
	}

	function onBack() {
		history.push("/search");
	}

	return (
		<>
			<IonLoading isOpen={loading} message={t("searchResults.loading")} />
			<IonMenu contentId="main-content">
				<IonHeader>
					<IonToolbar>
						<IonTitle>{t("searchResults.menuTitle")}</IonTitle>
					</IonToolbar>
				</IonHeader>
				<IonContent className="ion-padding">
					<IonItem>
						<IonLabel position="stacked">
							{t("searchResults.textInputLabel")}
						</IonLabel>
						<IonInput onIonChange={onFilterChange} />
					</IonItem>
					<IonMenuToggle>
						<IonButton expand="block">
							{t("searchResults.applyFilter")}
						</IonButton>
					</IonMenuToggle>
				</IonContent>
			</IonMenu>
			<IonPage id="main-content">
				<IonHeader>
					<IonToolbar>
						<IonButtons slot="start">
							{/* <IonBackButton defaultHref='/search' /> */}
							<IonButton onClick={onBack} size="large">
								<IonIcon
									icon={arrowBackOutline}
									slot="icon-only"
								/>
							</IonButton>
						</IonButtons>
						<IonTitle>{t("searchResults.results")}</IonTitle>
						<IonButtons slot="end">
							<IonButton color="danger" onClick={onReject}>
								<IonIcon icon={thumbsDown} slot="icon-only" />
							</IonButton>
							{/* <IonMenuButton /> */}
						</IonButtons>
					</IonToolbar>
				</IonHeader>
				<IonContent role="feed">
					{!loading && searchResults.length === 0 && (
						<IonItem>
							<IonText>
								{t("searchResults.noSearchResults")}
							</IonText>
						</IonItem>
					)}
					<IonList>
						{map(
							searchResults,
							(result, resultIndex) => (
								<IonItem
									key={`${result.barcode}-item-${resultIndex}`}
									style={{
										marginBottom: "20px",
									}}
								>
									<IonText
										style={{
											marginRight: "5px",
										}}
									>
										{resultIndex + 1}.
									</IonText>
									<Swiper
										lazy={true}
										preloadImages={false}
										spaceBetween={50}
									>
										{map(
											result.photos,
											(photo, sliderIndex) => (
												<SwiperSlide
													key={`${result.barcode}-slide-${sliderIndex}`}
													onClick={() =>
														onClickSlide(
															result.barcode,
															resultIndex
														)
													}
													style={{
														marginBottom: "10px",
													}}
												>
													<IonImg
														src={photo}
														style={{
															height: "250px",
														}}
													/>
												</SwiperSlide>
											)
										)}
									</Swiper>
								</IonItem>
							)
						)}
					</IonList>
					<IonInfiniteScroll
						disabled={disableInfiniteScroll}
						onIonInfinite={onInfiniteScroll}
					>
						<IonInfiniteScrollContent></IonInfiniteScrollContent>
					</IonInfiniteScroll>
				</IonContent>
				<IonToast
					isOpen={!isEmpty(toastMessage)}
					message={toastMessage}
					duration={DEFAULT_TOAST_DURATION}
					onDidDismiss={() => setToastMessage("")}
				/>
			</IonPage>
		</>
	);
}
